import { useEffect, useRef, useState } from 'react'
import { getSettings, saveSettings, getVatStatus, type Settings as SettingsT, type VatStatus } from '../api'
import { useDirtyGuard, stableJson, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import Panel from '../components/Panel'
import InfoTip from '../components/InfoTip'
import { EU_COUNTRIES } from '../lookups'
import { checkBeforeSave } from '../forms/checkForm'
import { currencySymbol } from '../../shared/currency'
import { OSS_THRESHOLD_EUR, OSS_THRESHOLD_STEP, OSS_OVER_THRESHOLD_COLOR, DEFAULT_BASE_COUNTRY } from '../../shared/constants'
import { fill, PAGES_VAT_SETTINGS_TEXTS as T } from '../../shared/texts'

// The OSS threshold is stated in EUR by law (10.000 €) — deliberately NOT the backoffice currency.
const OSS_CURRENCY = 'EUR'
const eur = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: OSS_CURRENCY, maximumFractionDigits: 0 }).format(n)

// Finance settings page: how VAT is charged (own seat / customer country / auto over a
// turnover threshold), the OSS turnover monitor and the editable per-country rate table.
// Saves only the VAT fields via the shared settings endpoint (partial patch).
export default function VatSettings({ tabId }: { tabId?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { toast } = useToast()
    const { can } = useAuth()
    const readOnly = !can('settings', 'write')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [vatStatus, setVatStatus] = useState<VatStatus | null>(null)

    const [form, setForm] = useState({
        vatOssMode: 'off' as 'off' | 'manual' | 'auto',
        vatBaseCountry: '',
        vatOssThreshold: OSS_THRESHOLD_EUR,
        vatRateOverrides: {} as Record<string, number>,
    })
    const [baseline, setBaseline] = useState<string | null>(null)
    // stableJson, not JSON.stringify: clearing a country's rate and typing it again
    // moves that key to the end of vatRateOverrides — unchanged rates that read as
    // edited otherwise.
    const dirty = baseline !== null && stableJson(form) !== baseline

    function apply(data: SettingsT) {
        const next = {
            vatOssMode: data.vatOssMode,
            vatBaseCountry: data.vatBaseCountry,
            vatOssThreshold: data.vatOssThreshold ?? OSS_THRESHOLD_EUR,
            vatRateOverrides: { ...data.vatRateOverrides },
        }
        setForm(next); setBaseline(stableJson(next))
    }
    function refreshStatus() { getVatStatus().then(setVatStatus).catch(() => {}) }

    useEffect(() => {
        getSettings().then((d) => { apply(d); setLoaded(true) }).catch((e) => setError(e?.message ?? T.loadFailed))
        refreshStatus()
    }, [])

    function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
        setForm((f) => ({ ...f, [key]: value }))
    }

    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setSaving(true); setError('')
        try {
            const saved = await saveSettings(form) // partial patch — only these VAT fields
            apply(saved); refreshStatus()
            toast(T.saved)
            return { ok: true }
        } catch (e: any) {
            const m = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(m); return { ok: false, error: m }
        } finally { setSaving(false) }
    }
    function onSave(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !readOnly, save)

    if (!loaded) return <div><h1>{T.title}</h1><p className="muted">{error || T.loading}</p></div>

    const resolvedBase = vatStatus?.baseCountry ?? DEFAULT_BASE_COUNTRY
    const resolvedBaseName = EU_COUNTRIES.find((c) => c.code === resolvedBase)?.name ?? resolvedBase

    return (
        <form ref={formRef} noValidate onSubmit={onSave} className="form-page">
            <div className="form-top">
                <div className="form-header">
                    <h1>{readOnly ? T.titleReadOnly : T.title}</h1>
                    {!readOnly && <button type="submit" disabled={saving || !dirty}>{saving ? '…' : T.apply}</button>}
                </div>
                {readOnly && <div className="notice">{T.readOnlyNotice}</div>}
                {error && <div className="notice err">{error}</div>}
            </div>

            <fieldset disabled={readOnly} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <Panel title={T.calcPanel}>
                    <div className="row">
                        <div className="field">
                            <label>{T.calcLabel} <InfoTip>{T.calcTip}</InfoTip></label>
                            <select value={form.vatOssMode} style={{ width: 240 }}
                                onChange={(e) => set('vatOssMode', e.target.value as 'off' | 'manual' | 'auto')}>
                                <option value="off">{T.companySeat}</option>
                                <option value="manual">{T.modeManual}</option>
                                <option value="auto">{T.modeAuto}</option>
                            </select>
                        </div>
                        <div className="field">
                            <label>{T.companySeat} <InfoTip>{T.baseTip}</InfoTip></label>
                            <select value={form.vatBaseCountry} style={{ width: 240 }}
                                onChange={(e) => set('vatBaseCountry', e.target.value)}>
                                <option value="">{fill(T.baseDefault, { name: resolvedBaseName, code: resolvedBase })}</option>
                                {EU_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                            </select>
                        </div>
                        <div className="field">
                            <label>{fill(T.thresholdLabel, { symbol: currencySymbol(OSS_CURRENCY) })} <InfoTip>{fill(T.thresholdTip, { amount: eur(OSS_THRESHOLD_EUR) })}</InfoTip></label>
                            <input type="number" min={0} step={OSS_THRESHOLD_STEP} style={{ width: 140 }}
                                value={form.vatOssThreshold}
                                onChange={(e) => set('vatOssThreshold', Number(e.target.value))} />
                        </div>
                    </div>

                    {vatStatus && (() => {
                        const rev = Number(vatStatus.revenueYtd) || 0
                        const thr = Number(vatStatus.threshold) || OSS_THRESHOLD_EUR
                        const pct = thr > 0 ? Math.min(100, Math.round((rev / thr) * 100)) : 0
                        const over = rev >= thr
                        return (
                            <div style={{ marginTop: 8 }}>
                                <div className="chart-total">
                                    {fill(T.revenue, { year: vatStatus.year ?? new Date().getFullYear(), revenue: eur(rev), threshold: eur(thr), pct })}
                                    {' · '}{vatStatus.ossEnabled ? T.ossActive : T.ossInactive}
                                </div>
                                <div style={{ background: 'var(--line)', borderRadius: 999, height: 10, overflow: 'hidden', marginTop: 4 }}>
                                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: over ? OSS_OVER_THRESHOLD_COLOR : 'var(--accent)' }} />
                                </div>
                                {form.vatOssMode !== 'off' && <p className="muted" style={{ marginTop: 6 }}>{T.registrationHint}</p>}
                                {over && form.vatOssMode === 'auto' && <p className="muted" style={{ marginTop: 6 }}>{T.thresholdExceeded}</p>}
                            </div>
                        )
                    })()}
                </Panel>

                <Panel title={T.ratesPanel}>
                    <p className="muted" style={{ marginTop: 0 }}>{T.ratesIntro}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                        {EU_COUNTRIES.map((c) => {
                            const def = vatStatus?.defaults?.[c.code]
                            return (
                                <div key={c.code} className="field" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: 0 }}>
                                    <span>{c.name} <span className="muted">({c.code})</span></span>
                                    <input type="number" min={0} max={100} step={0.1} style={{ width: 90 }}
                                        placeholder={def != null ? String(def) : ''}
                                        value={form.vatRateOverrides[c.code] ?? ''}
                                        onChange={(e) => {
                                            const next = { ...form.vatRateOverrides }
                                            if (e.target.value === '') delete next[c.code]
                                            else next[c.code] = Number(e.target.value)
                                            set('vatRateOverrides', next)
                                        }} />
                                </div>
                            )
                        })}
                    </div>
                </Panel>
            </fieldset>
        </form>
    )
}
